import React, { useContext } from 'react';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useParams } from 'react-router-dom';
import { LoginApiContext } from '../api/LoginApiContext';
import Spinner from '../components/spinner/spinner';
import PageLayout from '../pagelayout';
import { BackButton } from '../pagelayout/backButton';
import { useIntl } from 'react-intl';
import translations from './userprofile.i18n';
import {
  BrukerrollerDto,
  HttpError,
} from '../api/generated/studiested-generated-api';
import StudiestedUserprofile from './studiestedUserprofile';
import Tilganger from './studiestedbrukerTilganger';
import { UhaUserContext } from '../user/UhaUserStore';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import Activities from './activities.uha';
import PageNotFoundPage from '../pages/pagenotfound';

const StudiestedbrukerprofilPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { uhaUser } = useContext(UhaUserContext);
  const adminUser =
    uhaUser && uhaUser.hoeyesteRettighet === BrukerrollerDto.Administrator;

  const { loginApi } = useContext(LoginApiContext);
  const { studiestedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const { id } = useParams<{ id: string }>();

  const { data: userrole } = useLoader(() => loginApi.userroleGet(), []);
  const studiestedNavn = (userrole && userrole.selectedRole!!.name) || '';
  const isSelf = userrole && userrole.selectedRole!.id === id;
  const canAdministerPermissions = (userrole && !isSelf && adminUser) || false;

  const { data, reload } = useLoader(
    () =>
      studiestedApi.studiestedbrukerprofilIdGet({
        pathParams: { id: id as string },
      }),
    [id],
    true,
    true,
  );

  const { formatMessage } = useIntl();
  const labels = {
    pageTitle: formatMessage({ id: translations.pageTitleUHUser.id }),
    title: formatMessage(
      { id: translations.titleStudiested.id },
      {
        institusjon: studiestedNavn,
      },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    tilbake: formatMessage({ id: translations.toInstitution.id }),
    lagret: formatMessage({ id: translations.lagret.id }),
  };

  const saveStudiestedbrukerprofil = (
    omMeg: string,
    epost: string,
    telefonnummer: string,
  ) => {
    studiestedApi
      .studiestedbrukerprofilIdPut({
        pathParams: { id: id as string },
        studiestedbrukerprofilPutDto: {
          omMeg,
          epost,
          telefonnummer,
        },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: formatMessage({ id: translations.lagret.id }),
        });
        reload();
      })
      .catch((err: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        }),
      );
  };

  const uhBrukerProfile = React.useMemo(() => {
    return data ? { ...data, studiestedNavn } : undefined;
  }, [data, studiestedNavn]);

  const { fornavn: fnavn, etternavn: enavn } = uhBrukerProfile?.bruker || {
    fornavn: '',
    etternavn: '',
  };

  const userName = `${fnavn} ${enavn}`;

  if (!userrole || !data) {
    return <Spinner />;
  }

  if (!id) {
    return <PageNotFoundPage />;
  }

  return (
    <PageLayout
      isIndented
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={
        <BackButton title={labels.tilbake} path={'/myinstitution'} />
      }
    >
      <StudiestedUserprofile
        brukerprofil={uhBrukerProfile!}
        saveHandler={saveStudiestedbrukerprofil}
        editableFields={['omMeg']}
      />
      <Tilganger
        brukerprofil={uhBrukerProfile!}
        canAdminister={canAdministerPermissions}
        reloadBrukerprofil={reload}
      />
      <Activities
        userProfileId={id}
        userName={userName}
        isSelf={isSelf!!}
        isPraksisstedBruker={false}
      />
    </PageLayout>
  );
};

export default StudiestedbrukerprofilPage;
